import {
  ComputerIcon,
  getIcon,
  TIconColor,
  TTranslator,
  WorkerIcon,
} from '@smartshell/ui-kit';
import cn from 'classnames';
import dayjs from 'dayjs';
import { Column, Row } from 'react-table';

import { TLocalesKeys } from 'localization';
import { ELogType, TEventListItem, ILogConfig } from 'types';

import s from '../EventLogsTable.module.scss';
import { getGuest } from './getGuest';
import { getLogDescription } from './getLogDescription';

export const getLogsTableColumns = (
  t: TTranslator<TLocalesKeys>,
  LogConfig: Record<ELogType, ILogConfig>
): Array<Column<TEventListItem>> => [
  {
    Header: t('payment.date'),
    accessor: 'timestamp',
    Cell: ({ value }) => {
      const date = dayjs(value);
      return (
        <div className={s.date}>
          <span>
            {date.year() === dayjs().year()
              ? dayjs(value).format('DD MMM')
              : `${dayjs(value).format('DD MMM YY')} г.`}
          </span>
          <span className={s.time}>{date.format('HH:mm:ss')}</span>
        </div>
      );
    },
    width: 170,
    disableSortBy: true,
  },
  {
    Header: t('category'),
    accessor: ({ type }) => LogConfig[type],
    Cell: ({ value }: { value: ILogConfig }) => {
      const { title, iconName, color } = value;
      const Icon = getIcon(iconName);

      const colorIconMap = {
        green: 'green',
        orange: 'warning',
        blue: 'link-info',
        red: 'error',
        white: 'primary',
        gray: 'gray',
      };

      return (
        <div className={s.type}>
          {Icon && <Icon size="s" color={colorIconMap[color] as TIconColor} />}{' '}
          <span>{title}</span>
        </div>
      );
    },
    width: 320,
    disableResizing: true,
    disableSortBy: true,
  },
  {
    Header: t('logs.host'),
    accessor: ({ host }) => (host ? host : 'no pc'),
    Cell: ({ value }: { value: string }) => {
      if (value === 'no pc') return null;

      return (
        <div className={s.host}>
          <ComputerIcon size="s" />
          <span>{value}</span>
        </div>
      );
    },
    width: 220,
    disableResizing: true,
    disableSortBy: true,
  },
  {
    Header: t('logs.subject'),
    accessor: ({ subject }) => {
      if (!subject) return 'no subject';

      return subject.nickname ?? subject.phone;
    },
    Cell: ({ value, row }: { value: string; row: Row<TEventListItem> }) => {
      if (value === 'no subject') return getGuest(t, row.original.type, false);

      return (
        <div className={s.nickname}>
          <WorkerIcon size="s" />
          <span className={cn(s.user, 'subject')}>{value}</span>
        </div>
      );
    },
    width: 192,
    disableResizing: true,
  },
  {
    Header: t('logs.event'),
    accessor: ({ description }) => {
      if (!description) return 'no description';
      return description;
    },
    Cell: ({ row }: { row: Row<TEventListItem> }) => (
      <div className={s.description__wrapper}>
        <div className={s.description}>{getLogDescription(row)}</div>
      </div>
    ),
    minWidth: 645,
    disableSortBy: true,
  },
  {
    Header: t('logs.object'),
    accessor: ({ object }) => {
      if (!object) return 'no object';

      return object.nickname ?? object.phone;
    },
    Cell: ({ value, row }: { value: string; row: Row<TEventListItem> }) => {
      if (value === 'no object') return getGuest(t, row.original.type, true);

      return (
        <div className={s.nickname}>
          <WorkerIcon size="s" />
          <span className={cn(s.user, 'object')}>{value}</span>
        </div>
      );
    },
    width: 192,
    disableResizing: true,
  },
];
